import React, { useState, useEffect, useRef } from 'react';

interface Participant {
  id: string;
  name: string;
  role: 'host' | 'guest';
  status?: string;
  email?: string;
  phone?: string;
}

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: string;
  attachment?: { name: string; url: string; type: string } | null;
}

interface DockedRoomViewProps {
  roomId: string;
  currentUser: { id: string; name: string; role: 'host' | 'guest' };
  onLeave?: () => void;
}

export function DockedRoomView({ roomId = 'private', currentUser, onLeave }: DockedRoomViewProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [inputText, setInputText] = useState('');
  const [isMediaMinimized, setIsMediaMinimized] = useState(false); // Expanded by default so feeds are immediately visible
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [evictedReason, setEvictedReason] = useState<string | null>(null);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [showParticipantsDrawer, setShowParticipantsDrawer] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // --- SPRINT-MEDIA-CONTROLS-01: Focus, Pin & PiP State ---
  type FocusMode = 'auto' | 'ribbon' | 'gallery' | 'pin';
  const [focusMode, setFocusMode] = useState<FocusMode>('auto');
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [spotlightId, setSpotlightId] = useState<string | null>(null);
  const [isFocusPopoverOpen, setIsFocusPopoverOpen] = useState<boolean>(false);
  const [hideSelfView, setHideSelfView] = useState<boolean>(false);

  // --- PHASE 2: CONTENT-FIRST SCREEN SHARE & PAN/ZOOM ---
  type ContentLayoutMode = 'inset' | 'side-by-side' | 'content-only';
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [isScreenSharing, setIsScreenSharing] = useState<boolean>(false);
  const [contentLayout, setContentLayout] = useState<ContentLayoutMode>('inset');

  // Transmission Quality Profile State (1080p Text vs 720p Motion)
  type QualityProfile = 'text' | 'motion';
  const [qualityProfile, setQualityProfile] = useState<QualityProfile>('text');

  // WebRTC Telemetry State
  interface WebRtcTelemetry {
    bitrateKbps: number;
    fps: number;
    packetLoss: number;
    rttMs: number;
    resolution: string;
    candidateType: string;
    streamType: 'outbound' | 'inbound' | 'idle';
  }
  const [showStatsHud, setShowStatsHud] = useState<boolean>(false);
  const [rtcTelemetry, setRtcTelemetry] = useState<WebRtcTelemetry>({
    bitrateKbps: 0,
    fps: 0,
    packetLoss: 0,
    rttMs: 0,
    resolution: '--',
    candidateType: '--',
    streamType: 'idle',
  });
  const prevStatsBytesRef = useRef<{ bytes: number; timestamp: number }>({ bytes: 0, timestamp: 0 });

  useEffect(() => {
    if (!showStatsHud) return;

    const statsInterval = setInterval(async () => {
      const pc = peerConnRef.current;
      if (!pc || (pc.connectionState !== 'connected' && pc.iceConnectionState !== 'connected')) {
        setRtcTelemetry((prev) => ({ ...prev, streamType: 'idle' }));
        return;
      }

      try {
        const stats = await pc.getStats();
        let bytes = 0;
        let fps = 0;
        let packetsLost = 0;
        let rtt = 0;
        let res = '--';
        let candType = '--';
        let sType: 'outbound' | 'inbound' | 'idle' = 'idle';
        let now = Date.now();

        stats.forEach((report: any) => {
          if (report.type === 'candidate-pair' && (report.selected || report.state === 'succeeded')) {
            if (report.currentRoundTripTime !== undefined) {
              rtt = Math.round(report.currentRoundTripTime * 1000);
            }
          }
          if (report.type === 'remote-candidate' && report.candidateType) {
            candType = report.candidateType;
          }
          // Outbound video (Host transmitting)
          if (report.type === 'outbound-rtp' && report.kind === 'video') {
            bytes = report.bytesSent || 0;
            if (report.framesPerSecond !== undefined) fps = report.framesPerSecond;
            now = report.timestamp || Date.now();
            sType = 'outbound';
            if (report.frameWidth && report.frameHeight) {
              res = `${report.frameWidth}x${report.frameHeight}`;
            }
          }
          // Inbound video (Guest receiving)
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            bytes = report.bytesReceived || 0;
            if (report.framesPerSecond !== undefined) fps = report.framesPerSecond;
            packetsLost = report.packetsLost || 0;
            now = report.timestamp || Date.now();
            sType = 'inbound';
            if (report.frameWidth && report.frameHeight) {
              res = `${report.frameWidth}x${report.frameHeight}`;
            }
          }
        });

        // Compute instantaneous bitrate
        let bitrateKbps = 0;
        if (prevStatsBytesRef.current.timestamp > 0 && now > prevStatsBytesRef.current.timestamp) {
          const deltaSec = (now - prevStatsBytesRef.current.timestamp) / 1000;
          bitrateKbps = Math.round(((bytes - prevStatsBytesRef.current.bytes) * 8) / deltaSec / 1000);
          if (bitrateKbps < 0) bitrateKbps = 0;
        }
        prevStatsBytesRef.current = { bytes, timestamp: now };

        setRtcTelemetry({
          bitrateKbps,
          fps: Math.round(fps),
          packetLoss: packetsLost,
          rttMs: rtt,
          resolution: res,
          candidateType: candType,
          streamType: sType,
        });
      } catch (err) {
        // Stats read error fallback
      }
    }, 1000);

    return () => clearInterval(statsInterval);
  }, [showStatsHud]);

  const applyQualityProfile = async (profile: QualityProfile, activeStream?: MediaStream | null) => {
    setQualityProfile(profile);
    const stream = activeStream || screenStream;
    if (!stream) return;

    const screenTrack = stream.getVideoTracks()[0];
    if (screenTrack) {
      try {
        // 1. W3C contentHint signaling for hardware encoder tuning
        if ('contentHint' in screenTrack) {
          (screenTrack as any).contentHint = profile === 'text' ? 'detail' : 'motion';
        }

        // 2. Dynamic track constraints
        await screenTrack.applyConstraints({
          width: { max: profile === 'text' ? 1920 : 1280 },
          height: { max: profile === 'text' ? 1080 : 720 },
          frameRate: { max: profile === 'text' ? 15 : 30 },
        });
        console.log(`[ScreenQuality] Applied hardware constraints for ${profile} mode.`);
      } catch (err) {
        console.warn('[ScreenQuality] applyConstraints notice:', err);
      }
    }

    // 3. Dynamic WebRTC Sender parameter modulation (bitrate & framerate cap)
    if (peerConnRef.current) {
      try {
        const videoSender = peerConnRef.current.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (videoSender) {
          const params = videoSender.getParameters();
          if (!params.encodings || params.encodings.length === 0) {
            params.encodings = [{}];
          }
          if (profile === 'text') {
            params.encodings[0].maxBitrate = 3000000; // 3.0 Mbps for sharp 1080p text
            params.encodings[0].maxFramerate = 15;
            params.encodings[0].scaleResolutionDownBy = 1.0;
          } else {
            params.encodings[0].maxBitrate = 1800000; // 1.8 Mbps for 720p motion
            params.encodings[0].maxFramerate = 30;
            params.encodings[0].scaleResolutionDownBy = 1.5;
          }
          await videoSender.setParameters(params);
          console.log(`[ScreenQuality] RTCRtpSender encodings updated to ${profile} mode.`);
        }
      } catch (err) {
        console.warn('[ScreenQuality] setParameters notice:', err);
      }
    }
  };
  
  // Pan & Zoom Engine State
  const [zoomScale, setZoomScale] = useState<number>(1);
  const [panPos, setPanPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDraggingPan, setIsDraggingPan] = useState<boolean>(false);
  const dragStartRef = useRef<{ x: number; y: number; startPanX: number; startPanY: number }>({ x: 0, y: 0, startPanX: 0, startPanY: 0 });
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);

  // Phase 3: Web Audio Mixer Refs & State
  const audioContextRef = useRef<AudioContext | null>(null);
  const mixedAudioTrackRef = useRef<MediaStreamTrack | null>(null);
  const [hasScreenAudio, setHasScreenAudio] = useState<boolean>(false);

  // --- PHASE 4: SHARED LASER POINTER & SVG ANNOTATIONS ---
  type AnnotationTool = 'none' | 'laser' | 'pen' | 'highlighter';
  interface AnnotationStroke {
    id: string;
    userId: string;
    color: string;
    width: number;
    opacity: number;
    points: { x: number; y: number }[];
  }
  interface RemotePointer {
    name: string;
    color: string;
    x: number;
    y: number;
    lastActive: number;
  }

  const [annotationTool, setAnnotationTool] = useState<AnnotationTool>('none');
  const [annotationColor, setAnnotationColor] = useState<string>('#22d3ee');
  const [strokes, setStrokes] = useState<AnnotationStroke[]>([]);
  const [currentStroke, setCurrentStroke] = useState<AnnotationStroke | null>(null);
  const [remotePointers, setRemotePointers] = useState<Record<string, RemotePointer>>({});
  const [localLaser, setLocalLaser] = useState<{ x: number; y: number } | null>(null);

  const annotationDcRef = useRef<RTCDataChannel | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const screenStageContainerRef = useRef<HTMLDivElement | null>(null);

  // Broadcast helper (DataChannel + BroadcastChannel)
  const sendAnnotation = (msg: any) => {
    try {
      if (annotationDcRef.current && annotationDcRef.current.readyState === 'open') {
        annotationDcRef.current.send(JSON.stringify(msg));
      }
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage(msg);
      }
    } catch (e) {
      console.warn('[Annotation] Send error:', e);
    }
  };

  const handleAnnotationMessage = (data: any) => {
    if (!data || !data.type) return;
    if (data.type === 'pointer') {
      setRemotePointers((prev) => ({
        ...prev,
        [data.userId]: {
          name: data.name || 'User',
          color: data.color || '#ef4444',
          x: data.x,
          y: data.y,
          lastActive: Date.now(),
        },
      }));
    } else if (data.type === 'stroke_start') {
      setStrokes((prev) => [...prev, data.stroke]);
    } else if (data.type === 'stroke_point') {
      setStrokes((prev) =>
        prev.map((s) =>
          s.id === data.id ? { ...s, points: [...s.points, data.point] } : s
        )
      );
    } else if (data.type === 'clear') {
      setStrokes([]);
    }
  };

  // Setup BroadcastChannel for multi-tab and local peers
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      const bc = new BroadcastChannel('msng3r_screen_annotations');
      broadcastChannelRef.current = bc;
      bc.onmessage = (e) => handleAnnotationMessage(e.data);
      return () => {
        bc.close();
      };
    }
  }, []);

  // Inactivity cleaner: fade remote laser pointers after 2.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setRemotePointers((prev) => {
        let changed = false;
        const next: Record<string, RemotePointer> = {};
        for (const [id, ptr] of Object.entries(prev)) {
          if (now - ptr.lastActive <= 2500) {
            next[id] = ptr;
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 800);
    return () => clearInterval(timer);
  }, []);

  // Compute normalized coordinates (0..1000) inside stage container
  const getStageNormalizedCoords = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = screenStageContainerRef.current || e.currentTarget;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    const nx = Math.max(0, Math.min(1000, Math.round(((e.clientX - rect.left) / rect.width) * 1000)));
    const ny = Math.max(0, Math.min(1000, Math.round(((e.clientY - rect.top) / rect.height) * 1000)));
    return { x: nx, y: ny };
  };

  const handleStageMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (annotationTool === 'none') {
      handlePanMouseDown(e);
      return;
    }
    if (annotationTool === 'pen' || annotationTool === 'highlighter') {
      const pt = getStageNormalizedCoords(e);
      const stroke: AnnotationStroke = {
        id: 'st_' + Math.random().toString(36).slice(2, 9),
        userId: currentUser.id,
        color: annotationColor,
        width: annotationTool === 'highlighter' ? 14 : 3,
        opacity: annotationTool === 'highlighter' ? 0.35 : 1,
        points: [pt],
      };
      setCurrentStroke(stroke);
      setStrokes((prev) => [...prev, stroke]);
      sendAnnotation({ type: 'stroke_start', stroke });
    }
  };

  const handleStageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (annotationTool === 'none') {
      handlePanMouseMove(e);
      return;
    }
    const pt = getStageNormalizedCoords(e);
    if (annotationTool === 'laser') {
      setLocalLaser(pt);
      sendAnnotation({
        type: 'pointer',
        userId: currentUser.id,
        name: currentUser.name || 'Host',
        color: '#ef4444',
        x: pt.x,
        y: pt.y,
      });
    } else if (currentStroke) {
      setCurrentStroke((prev) => (prev ? { ...prev, points: [...prev.points, pt] } : null));
      setStrokes((prev) =>
        prev.map((s) => (s.id === currentStroke.id ? { ...s, points: [...s.points, pt] } : s))
      );
      sendAnnotation({ type: 'stroke_point', id: currentStroke.id, point: pt });
    }
  };

  const handleStageMouseUp = () => {
    if (annotationTool === 'none') {
      handlePanMouseUp();
      return;
    }
    setCurrentStroke(null);
  };

  const handleStageMouseLeave = () => {
    handlePanMouseUp();
    setLocalLaser(null);
    setCurrentStroke(null);
  };

  // --- MOBILE TOUCH GESTURES ENGINE ---
  const touchStartDistRef = useRef<number | null>(null);
  const touchStartScaleRef = useRef<number>(1);
  const touchStartMidpointRef = useRef<{ x: number; y: number } | null>(null);
  const touchStartPanRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const touchSingleStartRef = useRef<{ x: number; y: number } | null>(null);

  const getTouchDist = (t1: React.Touch, t2: React.Touch) =>
    Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

  const getTouchMid = (t1: React.Touch, t2: React.Touch) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const getStageNormalizedCoordsFromTouch = (touch: React.Touch) => {
    const el = screenStageContainerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    const nx = Math.max(0, Math.min(1000, Math.round(((touch.clientX - rect.left) / rect.width) * 1000)));
    const ny = Math.max(0, Math.min(1000, Math.round(((touch.clientY - rect.top) / rect.height) * 1000)));
    return { x: nx, y: ny };
  };

  const handleStageTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2) {
      // 2-Finger Pinch-to-Zoom & Dual-Pan Start
      setCurrentStroke(null);
      const dist = getTouchDist(e.touches[0], e.touches[1]);
      const mid = getTouchMid(e.touches[0], e.touches[1]);
      touchStartDistRef.current = dist;
      touchStartScaleRef.current = zoomScale;
      touchStartMidpointRef.current = mid;
      touchStartPanRef.current = { ...panPos };
      return;
    }

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      if (annotationTool === 'none') {
        // Single finger pan drag
        touchSingleStartRef.current = { x: touch.clientX, y: touch.clientY };
        touchStartPanRef.current = { ...panPos };
      } else if (annotationTool === 'pen' || annotationTool === 'highlighter') {
        // Single finger draw stroke
        const pt = getStageNormalizedCoordsFromTouch(touch);
        const stroke: AnnotationStroke = {
          id: 'st_' + Math.random().toString(36).slice(2, 9),
          userId: currentUser.id,
          color: annotationColor,
          width: annotationTool === 'highlighter' ? 14 : 3,
          opacity: annotationTool === 'highlighter' ? 0.35 : 1,
          points: [pt],
        };
        setCurrentStroke(stroke);
        setStrokes((prev) => [...prev, stroke]);
        sendAnnotation({ type: 'stroke_start', stroke });
      } else if (annotationTool === 'laser') {
        // Single finger laser pointer
        const pt = getStageNormalizedCoordsFromTouch(touch);
        setLocalLaser(pt);
        sendAnnotation({
          type: 'pointer',
          userId: currentUser.id,
          name: currentUser.name || 'Mobile Host',
          color: '#ef4444',
          x: pt.x,
          y: pt.y,
        });
      }
    }
  };

  const handleStageTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2 && touchStartDistRef.current && touchStartMidpointRef.current) {
      // 2-Finger Pinch Zoom + Pan execution
      const currentDist = getTouchDist(e.touches[0], e.touches[1]);
      const currentMid = getTouchMid(e.touches[0], e.touches[1]);

      const scaleMultiplier = currentDist / touchStartDistRef.current;
      const nextScale = Math.min(4.0, Math.max(0.5, touchStartScaleRef.current * scaleMultiplier));
      setZoomScale(Number(nextScale.toFixed(2)));

      const deltaX = currentMid.x - touchStartMidpointRef.current.x;
      const deltaY = currentMid.y - touchStartMidpointRef.current.y;
      setPanPos({
        x: touchStartPanRef.current.x + deltaX,
        y: touchStartPanRef.current.y + deltaY,
      });
      return;
    }

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      if (annotationTool === 'none' && touchSingleStartRef.current) {
        const deltaX = touch.clientX - touchSingleStartRef.current.x;
        const deltaY = touch.clientY - touchSingleStartRef.current.y;
        setPanPos({
          x: touchStartPanRef.current.x + deltaX,
          y: touchStartPanRef.current.y + deltaY,
        });
      } else if (annotationTool === 'pen' || annotationTool === 'highlighter') {
        if (currentStroke) {
          const pt = getStageNormalizedCoordsFromTouch(touch);
          setCurrentStroke((prev) => (prev ? { ...prev, points: [...prev.points, pt] } : null));
          setStrokes((prev) =>
            prev.map((s) => (s.id === currentStroke.id ? { ...s, points: [...s.points, pt] } : s))
          );
          sendAnnotation({ type: 'stroke_point', id: currentStroke.id, point: pt });
        }
      } else if (annotationTool === 'laser') {
        const pt = getStageNormalizedCoordsFromTouch(touch);
        setLocalLaser(pt);
        sendAnnotation({
          type: 'pointer',
          userId: currentUser.id,
          name: currentUser.name || 'Mobile Host',
          color: '#ef4444',
          x: pt.x,
          y: pt.y,
        });
      }
    }
  };

  const handleStageTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length < 2) {
      touchStartDistRef.current = null;
      touchStartMidpointRef.current = null;
    }
    if (e.touches.length === 0) {
      touchSingleStartRef.current = null;
      setCurrentStroke(null);
      if (annotationTool === 'laser') {
        setLocalLaser(null);
      }
    }
  };

  const handleClearAnnotations = () => {
    setStrokes([]);
    sendAnnotation({ type: 'clear' });
  };

  const teardownAudioMixer = () => {
    try {
      if (mixedAudioTrackRef.current) {
        mixedAudioTrackRef.current.stop();
        mixedAudioTrackRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      setHasScreenAudio(false);

      // Revert to clean microphone track on WebRTC audio sender
      if (peerConnRef.current && localStreamRef.current) {
        const micTrack = localStreamRef.current.getAudioTracks()[0];
        const audioSender = peerConnRef.current.getSenders().find((s) => s.track && s.track.kind === 'audio');
        if (audioSender && micTrack) {
          audioSender.replaceTrack(micTrack).catch(console.warn);
        }
      }
    } catch (e) {
      console.warn('[Mixer] Teardown error:', e);
    }
  };

  const handleToggleScreenShare = async () => {
    if (isScreenSharing && screenStream) {
      screenStream.getTracks().forEach((t) => t.stop());
      setScreenStream(null);
      setIsScreenSharing(false);
      setZoomScale(1);
      setPanPos({ x: 0, y: 0 });
      teardownAudioMixer();

      // Restore camera track to WebRTC peer
      if (peerConnRef.current && localStreamRef.current) {
        const camTrack = localStreamRef.current.getVideoTracks()[0];
        const sender = peerConnRef.current.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender && camTrack) sender.replaceTrack(camTrack).catch(console.warn);
      }
      return;
    }

    try {
      // Phase 3: Request display media WITH tab/system audio
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: ({ cursor: 'always' } as any),
        audio: true,
      });

      setScreenStream(displayStream);
      setIsScreenSharing(true);
      applyQualityProfile(qualityProfile, displayStream);

      const screenVideoTrack = displayStream.getVideoTracks()[0];
      const screenAudioTrack = displayStream.getAudioTracks()[0];

      // Video track swapping
      if (peerConnRef.current && screenVideoTrack) {
        const sender = peerConnRef.current.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender) sender.replaceTrack(screenVideoTrack).catch(console.warn);
      }

      // Audio track mixing (Microphone + Tab/System Audio)
      if (screenAudioTrack) {
        setHasScreenAudio(true);
        try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          const audioCtx = new AudioContextClass();
          audioContextRef.current = audioCtx;
          const mixerDestination = audioCtx.createMediaStreamDestination();

          // Connect system/tab audio
          const sysSource = audioCtx.createMediaStreamSource(new MediaStream([screenAudioTrack]));
          const sysGain = audioCtx.createGain();
          sysGain.gain.value = 1.0;
          sysSource.connect(sysGain).connect(mixerDestination);

          // Mix microphone track if present
          if (localStreamRef.current) {
            const micTrack = localStreamRef.current.getAudioTracks()[0];
            if (micTrack) {
              const micSource = audioCtx.createMediaStreamSource(new MediaStream([micTrack]));
              const micGain = audioCtx.createGain();
              micGain.gain.value = 1.0;
              micSource.connect(micGain).connect(mixerDestination);
            }
          }

          const mixedTrack = mixerDestination.stream.getAudioTracks()[0];
          mixedAudioTrackRef.current = mixedTrack;

          // Replace audio sender with mixed audio track
          if (peerConnRef.current && mixedTrack) {
            const audioSender = peerConnRef.current.getSenders().find((s) => s.track && s.track.kind === 'audio');
            if (audioSender) {
              await audioSender.replaceTrack(mixedTrack);
              console.log('[Phase 3] Mixed system + microphone audio track is now live.');
            }
          }
        } catch (audioErr) {
          console.warn('[Phase 3] Audio mixer fallback:', audioErr);
        }
      } else {
        setHasScreenAudio(false);
      }

      screenVideoTrack.onended = () => {
        setScreenStream(null);
        setIsScreenSharing(false);
        setZoomScale(1);
        setPanPos({ x: 0, y: 0 });
        teardownAudioMixer();

        if (peerConnRef.current && localStreamRef.current) {
          const camTrack = localStreamRef.current.getVideoTracks()[0];
          const sender = peerConnRef.current.getSenders().find((s) => s.track && s.track.kind === 'video');
          if (sender && camTrack) sender.replaceTrack(camTrack).catch(console.warn);
        }
      };
    } catch (err) {
      console.warn('[ScreenShare] Cancelled or error:', err);
      teardownAudioMixer();
    }
  };

  // Pan Gestures
  const handlePanMouseDown = (e: React.MouseEvent) => {
    if (zoomScale <= 1) return;
    setIsDraggingPan(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startPanX: panPos.x,
      startPanY: panPos.y,
    };
  };

  const handlePanMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingPan || zoomScale <= 1) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPanPos({
      x: dragStartRef.current.startPanX + dx,
      y: dragStartRef.current.startPanY + dy,
    });
  };

  const handlePanMouseUp = () => setIsDraggingPan(false);

  const handleWheelZoom = (e: React.WheelEvent) => {
    if (!screenStream) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setZoomScale((prev) => {
      const next = Math.min(Math.max(1, prev + delta), 3.5);
      if (next === 1) setPanPos({ x: 0, y: 0 });
      return parseFloat(next.toFixed(2));
    });
  };

  const handleResetZoom = () => {
    setZoomScale(1);
    setPanPos({ x: 0, y: 0 });
  };
  const [hideCameraOff, setHideCameraOff] = useState<boolean>(false);

  const stageVideoRef = useRef<HTMLVideoElement | null>(null);
  const stageContainerRef = useRef<HTMLDivElement | null>(null);

  const handleTogglePiP = async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (stageVideoRef.current && document.pictureInPictureEnabled) {
        await stageVideoRef.current.requestPictureInPicture();
      }
    } catch (err) {
      console.warn('[PiP] Not available or rejected:', err);
    }
  };

  const handleToggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (stageContainerRef.current) {
      stageContainerRef.current.requestFullscreen();
    }
  };

  const togglePinParticipant = (id: string) => {
    setPinnedIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((p) => p !== id);
        if (next.length === 0 && focusMode === 'pin') setFocusMode('auto');
        return next;
      }
      if (prev.length >= 4) return prev; // max 4 pins
      setFocusMode('pin');
      return [...prev, id];
    });
  };

  const handleToggleSpotlight = (id: string) => {
    if (currentUser.role !== 'host') return;
    setSpotlightId((prev) => (prev === id ? null : id));
  };


  // Voice note recording & Emoji picker state
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<any>(null);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const peerConnRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const lastSignalTimeRef = useRef<number>(0);

  // Host Kick Guest Out & Invalidate Session
  const handleKickGuest = async (targetSessionId: string) => {
    try {
      await fetch(`/api/v1/invites/rooms/${roomId}/evict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetSessionId,
          reason: 'Host ended your session and cancelled your invite.',
        }),
      });
      setParticipants((prev) => prev.filter((p) => p.id !== targetSessionId));
    } catch (err) {
      console.warn('[Kick] Failed to evict participant:', err);
    }
  };

  // Host Revoke / Cancel Pending Invite
  const handleRevokeInvite = async (inviteId: string, tokenHash?: string) => {
    try {
      await fetch(`/api/v1/invites/rooms/${roomId}/invites/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inviteId, tokenHash }),
      });
      setPendingInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch (err) {
      console.warn('[Invite] Failed to revoke invite:', err);
    }
  };

  // Host Archive & Clear Session
  const handleArchiveRoom = async () => {
    if (!window.confirm('Archive current room session? This will download the chat transcript, evict all current guests, and reset the room for a fresh set of invitees.')) {
      return;
    }
    try {
      const res = await fetch(`/api/v1/invites/rooms/${roomId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.archive && Array.isArray(data.archive.messages)) {
          const exportText = data.archive.messages
            .map((m: any) => `[${m.timestamp}] ${m.senderName}: ${m.text}`)
            .join('\n');
          const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `msng3r-archive-${roomId}-${new Date().toISOString().slice(0, 10)}.txt`;
          a.click();
          URL.revokeObjectURL(url);
        }
        setMessages([{
          id: `sys_${Date.now()}`,
          senderId: 'system',
          senderName: 'System',
          text: 'Previous session archived and cleared. Old invites invalidated.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }]);
        setParticipants((prev) => prev.filter((p) => p.role === 'host'));
      }
    } catch (err) {
      console.warn('[Archive] Failed to archive room:', err);
    }
  };

  // 1. Sync Participants & Messages Every 1.5s
  useEffect(() => {
    let isMounted = true;

    async function syncRoom() {
      try {
        const [msgsRes, partsRes, invsRes] = await Promise.all([
          fetch(`/api/v1/invites/rooms/${roomId}/messages`),
          fetch(`/api/v1/invites/rooms/${roomId}/participants`),
          fetch(`/api/v1/invites/rooms/${roomId}/invites`),
        ]);

        if (msgsRes.ok) {
          const data = await msgsRes.json();
          if (isMounted && Array.isArray(data.messages)) {
            setMessages(data.messages);
          }
        }

        if (partsRes.ok) {
          const data = await partsRes.json();
          if (isMounted && Array.isArray(data.participants)) {
            setParticipants(data.participants);
          }
        }

        if (invsRes.ok) {
          const invData = await invsRes.json();
          if (isMounted && Array.isArray(invData.invites)) {
            setPendingInvites(invData.invites);
          }
        }
      } catch (err) {
        console.warn('[RoomSync] Poll warning:', err);
      }
    }

    syncRoom();
    const interval = setInterval(syncRoom, 1500);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [roomId]);

  // 2. WebRTC Peer AV Relay Setup
  useEffect(() => {
    let active = true;

    async function initWebRTC() {
      try {
        let iceServers: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
        try {
          const res = await fetch(`/api/v1/media/webrtc/turn-credentials?userDid=${encodeURIComponent(currentUser.id)}`);
          if (res.ok) {
            const data = await res.json();
            if (data.iceServers) iceServers = data.iceServers;
          }
        } catch {
          // Fallback to STUN
        }

        const pc = new RTCPeerConnection({ iceServers });
        peerConnRef.current = pc;
    // Phase 4: Attach RTCDataChannel for annotations
    try {
      pc.ondatachannel = (e) => {
        if (e.channel.label === 'msng3r_annotations') {
          annotationDcRef.current = e.channel;
          e.channel.onmessage = (evt) => {
            try { handleAnnotationMessage(JSON.parse(evt.data)); } catch (err) {}
          };
        }
      };
      const dc = pc.createDataChannel('msng3r_annotations');
      annotationDcRef.current = dc;
      dc.onmessage = (evt) => {
        try { handleAnnotationMessage(JSON.parse(evt.data)); } catch (err) {}
      };
    } catch (e) {
      console.warn('[Annotation] DataChannel creation deferred:', e);
    }

        // Obtain local camera/mic
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).catch(() => null);
        if (stream && active) {
          localStreamRef.current = stream;
          if (localVideoRef.current) localVideoRef.current.srcObject = stream;
          stream.getTracks().forEach((track) => pc.addTrack(track, stream));
        }

        // Attach remote track to remote video and audio elements
        pc.ontrack = (event) => {
          const stream = event.streams && event.streams[0] ? event.streams[0] : new MediaStream([event.track]);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = stream;
            remoteVideoRef.current.play().catch((err) => {
              console.warn('[WebRTC] Remote video playback deferred:', err);
            });
          }
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = stream;
            remoteAudioRef.current.play().catch(() => {});
          }
        };

        // Send local ICE candidates
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            fetch(`/api/v1/invites/rooms/${roomId}/signal`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                senderId: currentUser.id,
                signal: { type: 'candidate', candidate: event.candidate },
              }),
            }).catch(() => {});
          }
        };

        // Host creates Offer
        if (currentUser.role === 'host') {
          const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
          await pc.setLocalDescription(offer);
          await fetch(`/api/v1/invites/rooms/${roomId}/signal`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              senderId: currentUser.id,
              signal: { type: 'offer', sdp: offer.sdp },
            }),
          });
        } else {
          // Guest announces arrival to trigger fresh offer from host
          fetch(`/api/v1/invites/rooms/${roomId}/signal`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              senderId: currentUser.id,
              signal: { type: 'guest_ready' },
            }),
          }).catch(() => {});
        }
      } catch (e) {
        console.warn('[WebRTC] Relay initialization warning:', e);
      }
    }

    initWebRTC();

    // Signal polling loop
    const sigInterval = setInterval(async () => {
      try {
        const res = await fetch(`/api/v1/invites/rooms/${roomId}/signal?since=${lastSignalTimeRef.current}`);
        if (!res.ok) return;
        const data = await res.json();
        const pc = peerConnRef.current;
        if (!pc) return;

        for (const item of data.signals || []) {
          lastSignalTimeRef.current = Math.max(lastSignalTimeRef.current, item.timestamp);
          if (item.senderId === currentUser.id) continue;

          const sig = item.signal;
          if (!sig) continue;

          if (sig.type === 'GUEST_EVICTED' || sig.type === 'ROOM_ARCHIVED') {
            if (currentUser.role === 'guest' && (sig.targetSessionId === currentUser.id || !sig.targetSessionId || sig.type === 'ROOM_ARCHIVED')) {
              if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
              if (peerConnRef.current) peerConnRef.current.close();
              sessionStorage.removeItem('msng3r_guest_session');
              setEvictedReason(sig.reason || 'Host ended or archived your session');
              return;
            }
          } else if (sig.type === 'guest_ready' && currentUser.role === 'host') {
            const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
            await pc.setLocalDescription(offer);
            await fetch(`/api/v1/invites/rooms/${roomId}/signal`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ senderId: currentUser.id, signal: { type: 'offer', sdp: offer.sdp } }),
            });
          } else if (sig.type === 'offer') {
            if (pc.signalingState !== 'stable') {
              await pc.setLocalDescription({ type: 'rollback' }).catch(() => {});
            }
            await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: sig.sdp }));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            await fetch(`/api/v1/invites/rooms/${roomId}/signal`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ senderId: currentUser.id, signal: { type: 'answer', sdp: answer.sdp } }),
            });
          } else if (sig.type === 'answer' && pc.signalingState === 'have-local-offer') {
            await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: sig.sdp }));
          } else if (sig.type === 'candidate' && sig.candidate) {
            await pc.addIceCandidate(new RTCIceCandidate(sig.candidate)).catch(() => {});
          }
        }
      } catch {}
    }, 1500);

    return () => {
      active = false;
      clearInterval(sigInterval);
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
      if (peerConnRef.current) peerConnRef.current.close();
    };
  }, [roomId, currentUser.id, currentUser.role]);

  // Auto-scroll on new messages
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Send message
  const handleSend = async () => {
    if (!inputText.trim()) return;
    const textToSend = inputText.trim();
    setInputText('');

    try {
      await fetch(`/api/v1/invites/rooms/${roomId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderId: currentUser.id,
          senderName: currentUser.name,
          text: textToSend,
        }),
      });
    } catch (err) {
      console.warn('[Chat] Failed to send message:', err);
    }
  };

  // Upload attachment (images, media, docs)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const base64Data = reader.result as string;
      const isImg = file.type.startsWith('image/');

      try {
        await fetch(`/api/v1/invites/rooms/${roomId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            senderId: currentUser.id,
            senderName: currentUser.name,
            text: `Shared file: ${file.name}`,
            attachment: {
              name: file.name,
              url: base64Data,
              type: isImg ? 'image' : 'doc',
            },
          }),
        });
      } catch {}
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Start voice recording
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
      };

      recorder.start(200);
      mediaRecorderRef.current = recorder;
      setIsRecordingVoice(true);
      setRecordingSeconds(0);

      recordTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.warn('[VoiceNote] Failed to access mic:', err);
    }
  };

  // Cancel voice recording
  const cancelVoiceRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    clearInterval(recordTimerRef.current);
    audioChunksRef.current = [];
    setIsRecordingVoice(false);
    setRecordingSeconds(0);
  };

  // Stop recording and send voice note
  const sendVoiceRecording = () => {
    if (!mediaRecorderRef.current) return;
    clearInterval(recordTimerRef.current);

    mediaRecorderRef.current.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result as string;
        try {
          await fetch(`/api/v1/invites/rooms/${roomId}/messages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              senderId: currentUser.id,
              senderName: currentUser.name,
              text: `🎙️ Voice Note (${recordingSeconds}s)`,
              attachment: {
                name: `voice_${Date.now()}.webm`,
                url: base64Data,
                type: 'audio',
              },
            }),
          });
        } catch (err) {
          console.warn('[VoiceNote] Failed to send voice note:', err);
        }
      };
      reader.readAsDataURL(audioBlob);
      setIsRecordingVoice(false);
      setRecordingSeconds(0);
    };

    mediaRecorderRef.current.stop();
  };

  // Emoji insertion
  const handleEmojiSelect = (emoji: string) => {
    setInputText((prev) => prev + emoji);
    setShowEmojiPicker(false);
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsMuted(!track.enabled);
      }
    }
  };

  const toggleCam = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsVideoOff(!track.enabled);
      }
    }
  };

  if (evictedReason) {
    return (
      <div className="h-screen w-screen bg-neutral-950 flex items-center justify-center p-6 text-center text-white">
        <div className="max-w-md w-full p-8 bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-950/60 border border-red-800 flex items-center justify-center text-red-400 text-xl font-bold">
            ✕
          </div>
          <h3 className="text-lg font-bold">Entry Declined</h3>
          <p className="text-xs text-neutral-400">{evictedReason}</p>
          <button
            onClick={() => {
              sessionStorage.removeItem('msng3r_guest_session');
              window.location.href = '/';
            }}
            className="w-full py-2.5 text-xs font-semibold bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 rounded-xl text-neutral-300 transition"
          >
            Return to Entrance
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 max-h-full w-full flex-1 flex flex-col bg-neutral-950 text-white overflow-hidden select-none relative">
      
      {/* 1. ROOM HEADER */}
      <div className="flex items-center justify-between px-3 py-2 bg-neutral-900/90 border-b border-neutral-800 flex-shrink-0 z-20 backdrop-blur-md">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
          <span className="font-bold text-xs sm:text-sm text-cyan-400 font-mono flex-shrink-0">#{roomId}</span>
          <div className="flex items-center gap-1.5 overflow-x-auto max-w-[200px] sm:max-w-md no-scrollbar">
            {participants.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-mono bg-neutral-800 border border-neutral-700 text-neutral-200 flex-shrink-0"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="truncate max-w-[80px] sm:max-w-[120px]">{p.name}</span>
                {p.role === 'host' && <span className="text-cyan-400 text-[9px] font-sans font-semibold">(Host)</span>}
              </span>
            ))}
          </div>
        </div>

        {/* Header Right Actions */}
        <div className="flex items-center gap-1.5">
          {currentUser.role === 'host' && (
            <button
              type="button"
              onClick={handleArchiveRoom}
              className="px-2 py-1 text-xs bg-amber-950/80 hover:bg-amber-900 text-amber-300 border border-amber-800 rounded-lg hidden sm:flex items-center gap-1 transition"
              title="Archive transcript and reset room"
            >
              📦 Archive
            </button>
          )}
          {onLeave && (
            <button
              type="button"
              onClick={onLeave}
              className="px-2 py-1 text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-lg transition"
            >
              Exit
            </button>
          )}
        </div>
      </div>

      {/* 2. MAIN WORKSPACE: VIDEO STAGE + DEDICATED CHAT PANEL */}
      <div className="flex-1 min-h-0 min-w-0 flex overflow-hidden relative">
        
        {/* VIDEO STAGE: DYNAMIC FOCUS/GALLERY/RIBBON + PHASE 2 CONTENT STAGE */}
          <div 
            ref={stageContainerRef}
            className="flex-1 min-h-0 min-w-0 p-2 sm:p-3 flex flex-col justify-center items-center overflow-hidden relative"
          >
            {/* === PHASE 2: CONTENT-FIRST SCREEN SHARE ACTIVE === */}
            {screenStream ? (
              <div className="w-full h-full min-h-0 flex flex-col sm:flex-row gap-2 relative">
                {/* Main Content Presentation Canvas */}
                <div 
                  onMouseDown={handleStageMouseDown}
                  onMouseMove={handleStageMouseMove}
                  onMouseUp={handleStageMouseUp}
                  onMouseLeave={handleStageMouseLeave}
                  onWheel={handleWheelZoom}
                  onTouchStart={handleStageTouchStart}
                  onTouchMove={handleStageTouchMove}
                  onTouchEnd={handleStageTouchEnd}
                  onTouchCancel={handleStageTouchEnd}
                  className={`flex-1 min-h-0 min-w-0 relative rounded-2xl overflow-hidden bg-black border border-neutral-800 flex items-center justify-center select-none ${
                    zoomScale > 1 ? (isDraggingPan ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'
                  }`}
                >
                  <div
                    ref={screenStageContainerRef}
                    style={{
                      transform: `translate(${panPos.x}px, ${panPos.y}px) scale(${zoomScale})`,
                      transformOrigin: 'center center',
                      transition: isDraggingPan ? 'none' : 'transform 0.1s ease-out',
                    }}
                    className="w-full h-full relative flex items-center justify-center select-none"
                  >
                    <video
                      ref={(el) => {
                        screenVideoRef.current = el;
                        if (el && el.srcObject !== screenStream) {
                          el.srcObject = screenStream;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-contain pointer-events-none"
                    />

                    {/* WebRTC In-App Telemetry HUD Overlay */}
                    {showStatsHud && (
                      <div className="absolute top-12 right-3 z-30 bg-black/90 backdrop-blur-md border border-cyan-500/50 rounded-xl p-2.5 font-mono text-[10px] text-neutral-300 shadow-2xl pointer-events-none select-none min-w-[195px] animate-in fade-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-neutral-800 text-cyan-400 font-bold uppercase tracking-wider text-[9px]">
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            RTC Telemetry
                          </span>
                          <span className="text-[8px] px-1 py-0.2 rounded bg-neutral-800 text-neutral-400">
                            {rtcTelemetry.streamType}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          <div>
                            <span className="text-neutral-500 text-[9px]">Bitrate:</span>{' '}
                            <span className="text-emerald-400 font-bold">
                              {rtcTelemetry.bitrateKbps > 1000
                                ? `${(rtcTelemetry.bitrateKbps / 1000).toFixed(2)} Mb/s`
                                : `${rtcTelemetry.bitrateKbps} kb/s`}
                            </span>
                          </div>
                          <div>
                            <span className="text-neutral-500 text-[9px]">Cadence:</span>{' '}
                            <span className="text-cyan-300 font-bold">{rtcTelemetry.fps || '--'} fps</span>
                          </div>
                          <div>
                            <span className="text-neutral-500 text-[9px]">Res:</span>{' '}
                            <span className="text-white font-bold">{rtcTelemetry.resolution}</span>
                          </div>
                          <div>
                            <span className="text-neutral-500 text-[9px]">RTT:</span>{' '}
                            <span className={`font-bold ${rtcTelemetry.rttMs > 150 ? 'text-amber-400' : 'text-emerald-400'}`}>
                              {rtcTelemetry.rttMs} ms
                            </span>
                          </div>
                          <div>
                            <span className="text-neutral-500 text-[9px]">Loss:</span>{' '}
                            <span className={`font-bold ${rtcTelemetry.packetLoss > 0 ? 'text-red-400' : 'text-neutral-300'}`}>
                              {rtcTelemetry.packetLoss} pkts
                            </span>
                          </div>
                          <div>
                            <span className="text-neutral-500 text-[9px]">Route:</span>{' '}
                            <span className="text-neutral-300 uppercase">{rtcTelemetry.candidateType || 'direct'}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SVG Annotation & Collaborative Laser Layer */}
                    <svg
                      viewBox="0 0 1000 1000"
                      preserveAspectRatio="none"
                      className="absolute inset-0 w-full h-full pointer-events-none z-20"
                    >
                      {/* Committed Strokes */}
                      {strokes.map((st) => (
                        <polyline
                          key={st.id}
                          points={st.points.map((p) => `${p.x},${p.y}`).join(' ')}
                          stroke={st.color}
                          strokeWidth={st.width}
                          strokeOpacity={st.opacity}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      ))}

                      {/* Active Stroke Being Drawn */}
                      {currentStroke && (
                        <polyline
                          points={currentStroke.points.map((p) => `${p.x},${p.y}`).join(' ')}
                          stroke={currentStroke.color}
                          strokeWidth={currentStroke.width}
                          strokeOpacity={currentStroke.opacity}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      )}

                      {/* Remote Laser Pointers */}
                      {Object.entries(remotePointers).map(([uid, ptr]) => (
                        <g key={uid}>
                          <circle cx={ptr.x} cy={ptr.y} r="14" fill={ptr.color} fillOpacity="0.25" className="animate-ping" />
                          <circle cx={ptr.x} cy={ptr.y} r="6" fill={ptr.color} filter="drop-shadow(0 0 6px rgba(239,68,68,0.9))" />
                          <rect x={ptr.x + 8} y={ptr.y - 12} width={Math.max(42, ptr.name.length * 7 + 8)} height="16" rx="4" fill="rgba(15,23,42,0.85)" stroke={ptr.color} strokeWidth="1" />
                          <text x={ptr.x + 12} y={ptr.y} fill="#fff" fontSize="9" fontFamily="sans-serif" fontWeight="bold">
                            {ptr.name}
                          </text>
                        </g>
                      ))}

                      {/* Local Laser Pointer */}
                      {annotationTool === 'laser' && localLaser && (
                        <g>
                          <circle cx={localLaser.x} cy={localLaser.y} r="14" fill="#ef4444" fillOpacity="0.3" className="animate-ping" />
                          <circle cx={localLaser.x} cy={localLaser.y} r="6" fill="#ef4444" filter="drop-shadow(0 0 6px rgba(239,68,68,0.9))" />
                          <rect x={localLaser.x + 8} y={localLaser.y - 12} width="36" height="16" rx="4" fill="rgba(15,23,42,0.85)" stroke="#ef4444" strokeWidth="1" />
                          <text x={localLaser.x + 12} y={localLaser.y} fill="#fff" fontSize="9" fontFamily="sans-serif" fontWeight="bold">
                            You
                          </text>
                        </g>
                      )}
                    </svg>
                  </div>

                  {/* Pan & Zoom HUD Bar (Top Center) */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-neutral-900/90 border border-neutral-700/80 shadow-2xl backdrop-blur-md text-xs font-mono">
                    <span className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider mr-1">🖥️ Screen</span>
                    <button
                      type="button"
                      onClick={() => setZoomScale((s) => Math.max(1, parseFloat((s - 0.25).toFixed(2))))}
                      title="Zoom Out"
                      className="w-6 h-6 rounded-lg bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center text-neutral-200 transition"
                    >
                      −
                    </button>
                    <button
                      type="button"
                      onClick={handleResetZoom}
                      title="Reset Zoom"
                      className="px-2 h-6 rounded-lg bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center text-[10px] text-neutral-300 transition"
                    >
                      {Math.round(zoomScale * 100)}%
                    </button>
                    <button
                      type="button"
                      onClick={() => setZoomScale((s) => Math.min(3.5, parseFloat((s + 0.25).toFixed(2))))}
                      title="Zoom In"
                      className="w-6 h-6 rounded-lg bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center text-neutral-200 transition"
                    >
                      +
                    </button>

                    <div className="w-[1px] h-3.5 bg-neutral-700 mx-1" />

                    {/* Annotation & Pointer Toolbar */}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setAnnotationTool('none')}
                        title="Pan & Zoom Mode"
                        className={`p-1 w-6 h-6 rounded-lg text-xs flex items-center justify-center transition ${
                          annotationTool === 'none' ? 'bg-cyan-600 text-white font-bold' : 'text-neutral-400 hover:text-neutral-200'
                        }`}
                      >
                        🖐️
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnnotationTool('laser')}
                        title="Shared Laser Pointer"
                        className={`p-1 w-6 h-6 rounded-lg text-xs flex items-center justify-center transition ${
                          annotationTool === 'laser' ? 'bg-red-600 text-white font-bold animate-pulse' : 'text-neutral-400 hover:text-neutral-200'
                        }`}
                      >
                        🔴
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnnotationTool('pen')}
                        title="Neon Freehand Pen"
                        className={`p-1 w-6 h-6 rounded-lg text-xs flex items-center justify-center transition ${
                          annotationTool === 'pen' ? 'bg-emerald-600 text-white font-bold' : 'text-neutral-400 hover:text-neutral-200'
                        }`}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnnotationTool('highlighter')}
                        title="Semi-transparent Highlighter"
                        className={`p-1 w-6 h-6 rounded-lg text-xs flex items-center justify-center transition ${
                          annotationTool === 'highlighter' ? 'bg-amber-600 text-white font-bold' : 'text-neutral-400 hover:text-neutral-200'
                        }`}
                      >
                        🖍️
                      </button>
                      {(annotationTool === 'pen' || annotationTool === 'highlighter') && (
                        <button
                          type="button"
                          onClick={() => {
                            const colors = ['#22d3ee', '#10b981', '#f43f5e', '#facc15', '#a855f7'];
                            const nextIdx = (colors.indexOf(annotationColor) + 1) % colors.length;
                            setAnnotationColor(colors[nextIdx]);
                          }}
                          title="Cycle Ink Color"
                          className="w-4 h-4 rounded-full border border-white/50 ml-0.5 shadow transition"
                          style={{ backgroundColor: annotationColor }}
                        />
                      )}
                      {strokes.length > 0 && (
                        <button
                          type="button"
                          onClick={handleClearAnnotations}
                          title="Clear All Annotations"
                          className="px-1.5 h-6 rounded-lg bg-neutral-800 hover:bg-red-950 text-neutral-400 hover:text-red-300 text-[10px] transition"
                        >
                          🗑️
                        </button>
                      )}
                    </div>

                    <div className="w-[1px] h-3.5 bg-neutral-700 mx-1" />

                    {/* WebRTC Stats HUD Toggle */}
                    <button
                      type="button"
                      onClick={() => setShowStatsHud(!showStatsHud)}
                      title="Toggle WebRTC Telemetry HUD Overlay"
                      className={`px-1.5 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 transition ${
                        showStatsHud
                          ? 'bg-cyan-600 text-white font-bold shadow-md shadow-cyan-900/40'
                          : 'bg-neutral-800/80 text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      <span>📊</span>
                      <span className="hidden sm:inline">Stats</span>
                    </button>

                    <div className="w-[1px] h-3.5 bg-neutral-700 mx-0.5" />

                    {/* Quality Profile Selector (1080p Text vs 720p Motion) */}
                    <div className="flex items-center gap-0.5 bg-neutral-800/80 rounded-lg p-0.5 text-[10px]">
                      <button
                        type="button"
                        onClick={() => applyQualityProfile('text')}
                        title="1080p @ 15fps: High resolution detail for code, terminal & slides"
                        className={`px-1.5 py-0.5 rounded transition ${
                          qualityProfile === 'text'
                            ? 'bg-cyan-600 text-white font-bold'
                            : 'text-neutral-400 hover:text-neutral-200'
                        }`}
                      >
                        1080p Text
                      </button>
                      <button
                        type="button"
                        onClick={() => applyQualityProfile('motion')}
                        title="720p @ 30fps: Fluid framerate for motion, scrolling & video playback"
                        className={`px-1.5 py-0.5 rounded transition ${
                          qualityProfile === 'motion'
                            ? 'bg-cyan-600 text-white font-bold'
                            : 'text-neutral-400 hover:text-neutral-200'
                        }`}
                      >
                        720p Motion
                      </button>
                    </div>

                    <div className="w-[1px] h-3.5 bg-neutral-700 mx-1" />

                    {/* Content Layout Selector */}
                    <button
                      type="button"
                      onClick={() => setContentLayout('inset')}
                      title="Floating Inset"
                      className={`px-2 h-6 rounded-lg text-[10px] transition ${
                        contentLayout === 'inset' ? 'bg-cyan-600 text-white font-bold' : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      Inset
                    </button>
                    <button
                      type="button"
                      onClick={() => setContentLayout('side-by-side')}
                      title="Side-by-side Split"
                      className={`px-2 h-6 rounded-lg text-[10px] transition ${
                        contentLayout === 'side-by-side' ? 'bg-cyan-600 text-white font-bold' : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      Side
                    </button>
                    <button
                      type="button"
                      onClick={() => setContentLayout('content-only')}
                      title="Content Only"
                      className={`px-2 h-6 rounded-lg text-[10px] transition ${
                        contentLayout === 'content-only' ? 'bg-cyan-600 text-white font-bold' : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      Solo
                    </button>
                  </div>

                  {/* Stage Quick Actions (Top Right) */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 z-30">
                    <button
                      type="button"
                      onClick={() => {
                        if (screenVideoRef.current && document.pictureInPictureEnabled) {
                          screenVideoRef.current.requestPictureInPicture().catch(console.warn);
                        }
                      }}
                      title="Pop out Screen to PiP"
                      className="p-1.5 rounded-xl bg-black/60 hover:bg-neutral-800 text-neutral-300 hover:text-white border border-white/10 text-xs backdrop-blur-md transition"
                    >
                      📺
                    </button>
                    <button
                      type="button"
                      onClick={handleToggleFullscreen}
                      title="Fullscreen Content"
                      className="p-1.5 rounded-xl bg-black/60 hover:bg-neutral-800 text-neutral-300 hover:text-white border border-white/10 text-xs backdrop-blur-md transition"
                    >
                      ⛶
                    </button>
                  </div>
                </div>

                {/* Speaker Floating Inset (Inset Mode) */}
                {contentLayout === 'inset' && !hideSelfView && (
                  <div className="absolute bottom-4 right-4 w-36 sm:w-48 aspect-video rounded-xl bg-black border border-neutral-700/80 shadow-2xl overflow-hidden z-30 group">
                    <video
                      ref={(el) => {
                        if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
                          el.srcObject = localStreamRef.current;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      muted
                      playsInline
                      className={`w-full h-full object-cover transform -scale-x-100 ${isVideoOff ? 'hidden' : ''}`}
                    />
                    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-white">
                      You (Sharing)
                    </span>
                  </div>
                )}

                {/* Participants Side Strip (Side-by-Side Mode) */}
                {contentLayout === 'side-by-side' && (
                  <div className="w-full sm:w-48 shrink-0 flex sm:flex-col gap-2 overflow-auto p-1 bg-neutral-900/50 rounded-2xl border border-neutral-800">
                    {!hideSelfView && (
                      <div className="w-36 sm:w-full aspect-video rounded-xl bg-black border border-neutral-800 overflow-hidden relative shrink-0">
                        <video
                          ref={(el) => {
                            if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
                              el.srcObject = localStreamRef.current;
                              el.play().catch(() => {});
                            }
                          }}
                          autoPlay
                          muted
                          playsInline
                          className={`w-full h-full object-cover transform -scale-x-100 ${isVideoOff ? 'hidden' : ''}`}
                        />
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-white">
                          You
                        </span>
                      </div>
                    )}
                    {participants.filter((p) => p.id !== currentUser.id).map((p) => (
                      <div key={p.id} className="w-36 sm:w-full aspect-video rounded-xl bg-black border border-neutral-800 overflow-hidden relative shrink-0">
                        <video
                          ref={(el) => {
                            const s = remoteStream || remoteStreamRef.current;
                            if (el && s && el.srcObject !== s) {
                              el.srcObject = s;
                              el.play().catch(() => {});
                            }
                          }}
                          autoPlay
                          playsInline
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-white">
                          {p.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
                <>
                  {/* === MODE 1: EQUAL GALLERY === */}
            {focusMode === 'gallery' && (
              <div className={`w-full h-full min-h-0 min-w-0 grid gap-2 sm:gap-3 place-items-center ${
                hideSelfView || participants.filter((p) => p.id !== currentUser.id).length === 0
                  ? 'grid-cols-1'
                  : 'grid-cols-1 md:grid-cols-2'
              }`}>
                {/* Local Tile (if not hidden) */}
                {!hideSelfView && (
                  <div className="relative w-full h-full min-w-0 min-h-0 bg-black/95 rounded-2xl border border-neutral-800/80 overflow-hidden flex items-center justify-center shadow-xl">
                    <video
                      ref={(el) => {
                        localVideoRef.current = el;
                        if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
                          el.srcObject = localStreamRef.current;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      muted
                      playsInline
                      className={`w-full h-full object-contain transform -scale-x-100 ${isVideoOff ? 'hidden' : ''}`}
                    />
                    {isVideoOff && (
                      <div className="flex flex-col items-center justify-center gap-1 text-neutral-500">
                        <span className="text-2xl">🚫</span>
                        <span className="text-xs">Camera Off</span>
                      </div>
                    )}
                    <span className="absolute bottom-2 left-2 text-[10px] font-mono px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-md text-neutral-300 border border-white/5">
                      You ({currentUser.name}) {isMuted && '🔇'}
                    </span>
                  </div>
                )}

                {/* Remote Tile */}
                {participants.filter((p) => p.id !== currentUser.id).length === 0 ? (
                  <div className="relative w-full h-full min-w-0 min-h-0 bg-black/95 rounded-2xl border border-neutral-800/80 overflow-hidden flex items-center justify-center shadow-xl">
                    <div className="flex flex-col items-center justify-center text-center p-4 text-neutral-400 gap-1.5">
                      <span className="text-2xl animate-pulse">🚪</span>
                      <span className="text-xs font-semibold text-neutral-300">Waiting for Guest</span>
                      <span className="text-[10px] text-neutral-500 max-w-xs">
                        Share your invite link to begin private AV session
                      </span>
                    </div>
                  </div>
                ) : (
                  participants
                    .filter((p) => p.id !== currentUser.id)
                    .map((p) => (
                      <div
                        key={p.id}
                        className="group relative w-full h-full min-w-0 min-h-0 bg-black/95 rounded-2xl border border-neutral-800/80 overflow-hidden flex items-center justify-center shadow-xl"
                      >
                        <video
                          ref={(el) => {
                            remoteVideoRef.current = el;
                            const s = remoteStream || remoteStreamRef.current;
                            if (el && s && el.srcObject !== s) {
                              el.srcObject = s;
                              el.play().catch(() => {});
                            }
                          }}
                          autoPlay
                          playsInline
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute bottom-2 left-2 flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-md text-neutral-300 border border-white/5 pointer-events-none">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          <span>{p.name}</span>
                        </div>
                        {currentUser.role === 'host' && (
                          <div className="absolute inset-0 bg-neutral-950/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3 z-10">
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="text-xs font-bold text-white block">{p.name}</span>
                                <span className="text-[10px] text-emerald-400 font-mono">Live WebRTC</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleKickGuest(p.id)}
                                className="px-2.5 py-1 text-xs font-bold bg-red-600 hover:bg-red-500 text-white rounded-lg shadow-lg flex items-center gap-1 transition"
                              >
                                <span>🚫 Kick Out</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                )}
              </div>
            )}

            {/* === MODE 2: RIBBON + STAGE === */}
            {focusMode === 'ribbon' && (
              <div className="w-full h-full min-h-0 flex flex-col gap-2">
                {/* Main Stage */}
                <div className="flex-1 min-h-0 relative rounded-2xl overflow-hidden bg-black/95 border border-neutral-800/80 flex items-center justify-center shadow-xl">
                  {participants.filter((p) => p.id !== currentUser.id).length > 0 && !pinnedIds.includes('local') ? (
                    <video
                      ref={(el) => {
                        stageVideoRef.current = el;
                        remoteVideoRef.current = el;
                        const s = remoteStream || remoteStreamRef.current;
                        if (el && s && el.srcObject !== s) {
                          el.srcObject = s;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <video
                      ref={(el) => {
                        stageVideoRef.current = el;
                        localVideoRef.current = el;
                        if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
                          el.srcObject = localStreamRef.current;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      muted
                      playsInline
                      className={`w-full h-full object-contain transform -scale-x-100 ${isVideoOff ? 'hidden' : ''}`}
                    />
                  )}

                  {/* Stage Badges & Pop out PiP */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
                    <button
                      type="button"
                      onClick={handleTogglePiP}
                      title="Picture in Picture"
                      className="p-1.5 rounded-xl bg-black/60 hover:bg-neutral-800 text-neutral-300 hover:text-white border border-white/10 text-xs backdrop-blur-md transition"
                    >
                      📺
                    </button>
                    <button
                      type="button"
                      onClick={handleToggleFullscreen}
                      title="Fullscreen Stage"
                      className="p-1.5 rounded-xl bg-black/60 hover:bg-neutral-800 text-neutral-300 hover:text-white border border-white/10 text-xs backdrop-blur-md transition"
                    >
                      ⛶
                    </button>
                  </div>
                </div>

                {/* Bottom Filmstrip Ribbon */}
                <div className="h-28 shrink-0 flex items-center gap-2 overflow-x-auto px-2 py-1.5 bg-neutral-900/60 rounded-2xl border border-neutral-800/80 backdrop-blur-sm">
                  {/* Local Thumbnail in ribbon */}
                  {!hideSelfView && (
                    <div
                      onClick={() => togglePinParticipant('local')}
                      title="Pin your view to stage"
                      className="h-full aspect-video shrink-0 relative rounded-xl overflow-hidden bg-black border border-neutral-800 hover:border-cyan-500 cursor-pointer transition group"
                    >
                      <video
                        ref={(el) => {
                          if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
                            el.srcObject = localStreamRef.current;
                            el.play().catch(() => {});
                          }
                        }}
                        autoPlay
                        muted
                        playsInline
                        className={`w-full h-full object-cover transform -scale-x-100 ${isVideoOff ? 'hidden' : ''}`}
                      />
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-white">
                        You {pinnedIds.includes('local') && '📌'}
                      </span>
                    </div>
                  )}

                  {/* Remote Thumbnails in ribbon */}
                  {participants.filter((p) => p.id !== currentUser.id).map((p) => (
                    <div
                      key={p.id}
                      onClick={() => togglePinParticipant(p.id)}
                      title={`Pin ${p.name} to stage`}
                      className="h-full aspect-video shrink-0 relative rounded-xl overflow-hidden bg-black border border-neutral-800 hover:border-cyan-500 cursor-pointer transition group"
                    >
                      <video
                        ref={(el) => {
                          const s = remoteStream || remoteStreamRef.current;
                          if (el && s && el.srcObject !== s) {
                            el.srcObject = s;
                            el.play().catch(() => {});
                          }
                        }}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-white">
                        {p.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* === MODE 3: AUTO SPEAKER (Default) === */}
            {focusMode !== 'gallery' && focusMode !== 'ribbon' && (
              <div className="relative w-full h-full min-h-0 flex items-center justify-center">
                {/* Stage Video: Remote Peer fills stage if present */}
                {participants.filter((p) => p.id !== currentUser.id).length > 0 ? (
                  <div className="relative w-full h-full min-w-0 min-h-0 bg-black/95 rounded-2xl border border-neutral-800/80 overflow-hidden flex items-center justify-center shadow-xl">
                    <video
                      ref={(el) => {
                        stageVideoRef.current = el;
                        remoteVideoRef.current = el;
                        const s = remoteStream || remoteStreamRef.current;
                        if (el && s && el.srcObject !== s) {
                          el.srcObject = s;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      playsInline
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-lg bg-black/70 backdrop-blur-md text-neutral-300 border border-white/5 pointer-events-none">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span>{participants.find((p) => p.id !== currentUser.id)?.name || 'Guest'}</span>
                    </div>
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
                      <button
                        type="button"
                        onClick={handleTogglePiP}
                        title="Picture in Picture"
                        className="p-1.5 rounded-xl bg-black/60 hover:bg-neutral-800 text-neutral-300 hover:text-white border border-white/10 text-xs backdrop-blur-md transition"
                      >
                        📺
                      </button>
                      <button
                        type="button"
                        onClick={handleToggleFullscreen}
                        title="Fullscreen Stage"
                        className="p-1.5 rounded-xl bg-black/60 hover:bg-neutral-800 text-neutral-300 hover:text-white border border-white/10 text-xs backdrop-blur-md transition"
                      >
                        ⛶
                      </button>
                    </div>
                  </div>
                ) : (
                  /* When alone: Local view on stage */
                  <div className="relative w-full h-full min-w-0 min-h-0 bg-black/95 rounded-2xl border border-neutral-800/80 overflow-hidden flex items-center justify-center shadow-xl">
                    <video
                      ref={(el) => {
                        stageVideoRef.current = el;
                        localVideoRef.current = el;
                        if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
                          el.srcObject = localStreamRef.current;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      muted
                      playsInline
                      className={`w-full h-full object-contain transform -scale-x-100 ${isVideoOff ? 'hidden' : ''}`}
                    />
                    {isVideoOff && (
                      <div className="flex flex-col items-center justify-center gap-1 text-neutral-500">
                        <span className="text-2xl">🚫</span>
                        <span className="text-xs">Camera Off</span>
                      </div>
                    )}
                    <span className="absolute bottom-3 left-3 text-xs font-mono px-2.5 py-1 rounded-lg bg-black/70 backdrop-blur-md text-neutral-300 border border-white/5">
                      You ({currentUser.name}) {isMuted && '🔇'}
                    </span>
                  </div>
                )}

                {/* Floating Webcam Thumbnail (PiP overlay in Auto mode when remote peer is present) */}
                {participants.filter((p) => p.id !== currentUser.id).length > 0 && !hideSelfView && (
                  <div className="absolute bottom-4 right-4 w-36 sm:w-48 aspect-video rounded-xl bg-black border border-neutral-700/80 shadow-2xl overflow-hidden z-20 transition group">
                    <video
                      ref={(el) => {
                        localVideoRef.current = el;
                        if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
                          el.srcObject = localStreamRef.current;
                          el.play().catch(() => {});
                        }
                      }}
                      autoPlay
                      muted
                      playsInline
                      className={`w-full h-full object-cover transform -scale-x-100 ${isVideoOff ? 'hidden' : ''}`}
                    />
                    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-white">
                      You
                    </span>
                  </div>
                )}
              </div>
            )}
                </>
              )}
            </div>

          {/* DEDICATED CHAT PANEL (Sidebar on desktop / slide-over on mobile) */}
        {isChatOpen && (
          <div className="absolute md:relative top-0 right-0 bottom-0 w-80 sm:w-96 max-w-[90vw] bg-neutral-900/95 border-l border-neutral-800 flex flex-col z-40 shadow-2xl backdrop-blur-xl animate-in slide-in-from-right duration-200">
            <div className="p-3 border-b border-neutral-800 flex items-center justify-between bg-neutral-950/40">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-white">Room Chat</span>
                <span className="text-[10px] font-mono text-cyan-400">({messages.length})</span>
              </div>
              <button
                type="button"
                onClick={() => setIsChatOpen(false)}
                className="text-neutral-400 hover:text-white text-xs p-1 rounded-md"
              >
                ✕
              </button>
            </div>

            {/* Chat Messages Timeline */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3 overscroll-contain">
              {messages.length === 0 ? (
                <div className="text-center text-xs text-neutral-500 py-12">
                  No messages yet. Send a note or voice note below.
                </div>
              ) : (
                messages.map((m) => {
                  const isMine = m.senderId === currentUser.id;
                  return (
                    <div key={m.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                      <div className="flex items-baseline gap-1.5 mb-0.5">
                        <span className={`text-[10px] font-semibold ${isMine ? 'text-emerald-400' : 'text-cyan-400'}`}>
                          {isMine ? 'You' : m.senderName}
                        </span>
                        <span className="text-[9px] text-neutral-500 font-mono">{m.timestamp}</span>
                      </div>
                      <div className={`p-2.5 rounded-2xl max-w-[88%] text-xs ${
                        isMine
                          ? 'bg-cyan-600 text-white rounded-br-none'
                          : 'bg-neutral-800 text-neutral-200 rounded-bl-none border border-neutral-700/60'
                      }`}>
                        {m.text && <p className="break-words">{m.text}</p>}
                        {m.attachment && (
                          <div className="mt-1.5 pt-1.5 border-t border-white/10">
                            {m.attachment.type === 'audio' ? (
                              <audio controls src={m.attachment.url} className="h-8 max-w-[200px]" />
                            ) : m.attachment.type === 'image' ? (
                              <img src={m.attachment.url} alt={m.attachment.name} className="max-h-36 rounded-lg" />
                            ) : (
                              <a
                                href={m.attachment.url}
                                download={m.attachment.name}
                                className="text-[11px] underline font-mono break-all"
                              >
                                📄 {m.attachment.name}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={chatBottomRef} />
            </div>
          </div>
        )}

        {/* 4. MEMBERS & INVITED USERS DRAWER */}
        {showParticipantsDrawer && (
          <div className="absolute top-0 right-0 bottom-0 w-80 max-w-[90vw] bg-neutral-900 border-l border-neutral-800 flex flex-col flex-shrink-0 z-50 animate-in slide-in-from-right duration-200 shadow-2xl">
            <div className="p-3 border-b border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs text-white">Room Members & Invites</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-800 text-cyan-300">#{roomId}</span>
              </div>
              <button
                type="button"
                onClick={() => setShowParticipantsDrawer(false)}
                className="text-neutral-400 hover:text-white text-xs p-1"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-neutral-400">
                  <span>In Session ({participants.length})</span>
                  <span className="text-emerald-400">Live</span>
                </div>
                <div className="space-y-1.5">
                  {participants.map((p) => (
                    <div
                      key={p.id}
                      className="group p-2.5 rounded-xl bg-neutral-950/70 border border-neutral-800 flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-neutral-800 border border-neutral-700 flex items-center justify-center text-xs font-bold text-neutral-300 flex-shrink-0">
                          {p.name.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-white truncate block">{p.name}</span>
                          <span className={`text-[9px] px-1.5 py-0.2 rounded font-mono ${p.role === 'host' ? 'bg-cyan-950 text-cyan-300' : 'bg-emerald-950 text-emerald-300'}`}>
                            {p.role === 'host' ? 'Host' : 'Guest'}
                          </span>
                        </div>
                      </div>
                      {currentUser.role === 'host' && p.role !== 'host' && (
                        <button
                          type="button"
                          onClick={() => handleKickGuest(p.id)}
                          className="px-2 py-1 text-[11px] font-bold bg-red-600 hover:bg-red-500 text-white rounded-lg"
                        >
                          🚫 Kick
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending Invites */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-neutral-400">
                  <span>Issued Invites ({pendingInvites.length})</span>
                  <span className="text-amber-400">Pending</span>
                </div>
                {pendingInvites.length === 0 ? (
                  <div className="text-[11px] text-neutral-500 italic p-2 bg-neutral-950/40 rounded-lg text-center">
                    No pending unredeemed invites
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {pendingInvites.map((inv) => (
                      <div
                        key={inv.id || inv.token_hash}
                        className="group p-2.5 rounded-xl bg-neutral-950/70 border border-neutral-800 flex items-center justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <div className="text-xs text-neutral-300 font-mono truncate">
                          <div className="text-xs text-neutral-300 font-mono truncate">🔑 {inv.token || `${inv.token_hash?.slice(0, 10)}...`}</div>
                          </div>
                          {inv.recipientEmail && <div className="text-[10px] text-cyan-300 font-mono truncate">✉️ {inv.recipientEmail}</div>}
                        </div>
                        {currentUser.role === 'host' && (
                          <button
                            type="button"
                            onClick={() => handleRevokeInvite(inv.id, inv.token_hash)}
                            className="px-2 py-1 text-[10px] bg-neutral-800 hover:bg-red-900 text-neutral-400 rounded-lg"
                          >
                            ✕ Cancel
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-3 border-t border-neutral-800 bg-neutral-950/50">
              <button
                type="button"
                onClick={handleArchiveRoom}
                className="w-full py-1.5 px-3 text-xs font-semibold bg-amber-950/80 hover:bg-amber-900 border border-amber-800 text-amber-300 rounded-lg transition"
              >
                📦 Archive & Clear Session
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 5. FLOATING MEDIA CONTROLS & BOTTOM COMPOSER DOCK */}
      <div
        className="flex-shrink-0 px-3 py-2 border-t border-neutral-800 bg-neutral-900/90 backdrop-blur-md z-30"
        style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-2 max-w-6xl mx-auto">
          
          {/* Hardware Action Buttons */}
          <div className="flex items-center gap-1.5 w-full md:w-auto justify-between md:justify-start">
            <button
              type="button"
              onClick={toggleMic}
              title={isMuted ? 'Unmute Mic' : 'Mute Mic'}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition ${
                isMuted
                  ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30'
                  : 'bg-neutral-800 border border-neutral-700 text-emerald-400 hover:bg-neutral-700'
              }`}
            >
              <span>{isMuted ? '🔇' : '🎙️'}</span>
              <span>{isMuted ? 'Unmute' : 'Mute'}</span>
            </button>

            <button
              type="button"
              onClick={toggleCam}
              title={isVideoOff ? 'Start Video' : 'Stop Video'}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition ${
                isVideoOff
                  ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30'
                  : 'bg-neutral-800 border border-neutral-700 text-neutral-200 hover:bg-neutral-700'
              }`}
            >
              <span>{isVideoOff ? '🚫' : '📹'}</span>
              <span>{isVideoOff ? 'Start Video' : 'Stop Video'}</span>
            </button>

              {/* [🖥️ Share Screen] Button */}
              <button
                type="button"
                id="btn-share-screen"
                onClick={handleToggleScreenShare}
                title={isScreenSharing ? 'Stop Sharing Screen' : 'Share Your Screen'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition ${
                  isScreenSharing
                    ? 'bg-emerald-950 border-emerald-500 text-emerald-300 shadow-lg shadow-emerald-950/50'
                    : 'bg-neutral-800 border-neutral-700 text-neutral-200 hover:bg-neutral-700'
                }`}
              >
                <span>🖥️</span>
                <span>{isScreenSharing ? 'Stop Share' : 'Share'}</span>
              </button>

              {/* [Focus ▾] Popover Control */}
              <div className="relative">
                <button
                  type="button"
                  id="btn-focus-mode"
                  onClick={() => setIsFocusPopoverOpen(!isFocusPopoverOpen)}
                  title="Layout & Focus Modes"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition ${
                    isFocusPopoverOpen || focusMode !== 'auto'
                      ? 'bg-cyan-950 border-cyan-500 text-cyan-300'
                      : 'bg-neutral-800 border-neutral-700 text-neutral-200 hover:bg-neutral-700'
                  }`}
                >
                  <span>👁️</span>
                  <span className="capitalize">{focusMode === 'auto' ? 'Focus' : focusMode}</span>
                  <span className="text-[9px] text-neutral-400">▾</span>
                </button>

                {isFocusPopoverOpen && (
                  <div
                    id="popover-focus-menu"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute bottom-12 left-0 z-50 w-56 p-2 rounded-2xl bg-neutral-900/95 border border-neutral-700 shadow-2xl backdrop-blur-xl text-neutral-200 flex flex-col gap-1 text-xs font-sans"
                  >
                    <div className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-neutral-400 border-b border-neutral-800 pb-1">
                      Viewing Intent
                    </div>
                    <button
                      type="button"
                      id="opt-auto-speaker"
                      onClick={() => { setFocusMode('auto'); setPinnedIds([]); setIsFocusPopoverOpen(false); }}
                      className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between transition ${
                        focusMode === 'auto' ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'hover:bg-neutral-800'
                      }`}
                    >
                      <span>⚡ Auto Speaker</span>
                      {focusMode === 'auto' && <span>✓</span>}
                    </button>
                    <button
                      type="button"
                      id="opt-ribbon"
                      onClick={() => { setFocusMode('ribbon'); setIsFocusPopoverOpen(false); }}
                      className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between transition ${
                        focusMode === 'ribbon' ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'hover:bg-neutral-800'
                      }`}
                    >
                      <span>🎞️ Ribbon + Stage</span>
                      {focusMode === 'ribbon' && <span>✓</span>}
                    </button>
                    <button
                      type="button"
                      id="opt-gallery"
                      onClick={() => { setFocusMode('gallery'); setIsFocusPopoverOpen(false); }}
                      className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between transition ${
                        focusMode === 'gallery' ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'hover:bg-neutral-800'
                      }`}
                    >
                      <span>⊞ Equal Gallery</span>
                      {focusMode === 'gallery' && <span>✓</span>}
                    </button>

                    <div className="px-2 pt-2 text-[10px] font-mono uppercase tracking-wider text-neutral-400 border-t border-neutral-800 pb-1 mt-1">
                      Floating & Display
                    </div>
                    <button
                      type="button"
                      id="opt-pip"
                      onClick={() => { handleTogglePiP(); setIsFocusPopoverOpen(false); }}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-neutral-800 flex items-center gap-2 transition"
                    >
                      <span>📺</span> Pop out PiP
                    </button>
                    <button
                      type="button"
                      id="opt-fullscreen"
                      onClick={() => { handleToggleFullscreen(); setIsFocusPopoverOpen(false); }}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-neutral-800 flex items-center gap-2 transition"
                    >
                      <span>⛶</span> Fullscreen Stage
                    </button>
                    <button
                      type="button"
                      id="opt-hide-self"
                      onClick={() => setHideSelfView(!hideSelfView)}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-neutral-800 flex items-center justify-between transition"
                    >
                      <span>Hide Self View</span>
                      <span className="text-[10px] text-neutral-400">{hideSelfView ? 'ON' : 'OFF'}</span>
                    </button>
                  </div>
                )}
              </div>

            <button
              type="button"
              onClick={() => setIsChatOpen(!isChatOpen)}
              title="Toggle Dedicated Chat Panel"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition ${
                isChatOpen
                  ? 'bg-blue-600/20 border-blue-500/40 text-blue-400'
                  : 'bg-neutral-800 border-neutral-700 text-neutral-200 hover:bg-neutral-700'
              }`}
            >
              <span>💬</span>
              <span>Chat</span>
              {messages.length > 0 && (
                <span className="px-1.5 py-0.2 rounded-full bg-blue-600 text-[10px] text-white font-mono">
                  {messages.length}
                </span>
              )}
            </button>

            {currentUser.role === 'host' && (
              <button
                type="button"
                onClick={() => setShowParticipantsDrawer(!showParticipantsDrawer)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition ${
                  showParticipantsDrawer
                    ? 'bg-cyan-950 border-cyan-700 text-cyan-300'
                    : 'bg-neutral-800 border-neutral-700 text-neutral-200 hover:bg-neutral-700'
                }`}
              >
                <span>👥</span>
                <span className="hidden sm:inline">Users</span>
                <span>({participants.length})</span>
              </button>
            )}

            {onLeave && (
              <button
                type="button"
                onClick={onLeave}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-medium ml-auto md:ml-0 transition"
              >
                <span>📞</span>
                <span>Leave</span>
              </button>
            )}
          </div>

          {/* Interactive Message Input & Voice Note Bar */}
          <div className="flex-1 w-full md:max-w-md flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt"
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Send image or file"
              className="p-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-xl transition text-xs flex-shrink-0"
            >
              📎
            </button>

            {/* Emoji Picker Popover */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                title="Insert emoji"
                className="p-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-xl transition text-xs flex-shrink-0"
              >
                😊
              </button>

              {showEmojiPicker && (
                <div className="absolute bottom-12 left-0 bg-neutral-900 border border-neutral-700 rounded-xl p-2 shadow-2xl z-50 flex gap-1.5 flex-wrap w-56">
                  {['😀', '😂', '🔥', '👍', '🚀', '🔒', '💬', '��', '❤️', '👏', '👀', '💯', '✨', '⚡'].map((em) => (
                    <button
                      key={em}
                      type="button"
                      onClick={() => handleEmojiSelect(em)}
                      className="hover:bg-neutral-800 p-1.5 rounded text-sm transition"
                    >
                      {em}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isRecordingVoice ? (
              <div className="flex-1 flex items-center justify-between px-3 py-1.5 bg-red-950/60 border border-red-800/80 rounded-xl text-xs text-red-200">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping" />
                  <span className="font-mono font-semibold">Recording: {recordingSeconds}s</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={cancelVoiceRecording}
                    className="px-2 py-0.5 rounded bg-neutral-800 text-neutral-300 text-[11px]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={sendVoiceRecording}
                    className="px-2 py-0.5 rounded bg-emerald-600 text-white font-medium text-[11px]"
                  >
                    Send 🎙️
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center bg-neutral-800/80 border border-neutral-700/60 rounded-xl px-2 py-1 focus-within:border-cyan-500 transition">
                <input
                  type="text"
                  placeholder={`Message #${roomId}...`}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  className="flex-1 bg-transparent border-none text-xs text-neutral-100 placeholder-neutral-500 focus:outline-none px-2 py-1"
                />

                {inputText.trim() ? (
                  <button
                    type="button"
                    onClick={handleSend}
                    className="px-2.5 py-1 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium transition"
                  >
                    Send
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startVoiceRecording}
                    className="p-1 text-neutral-400 hover:text-emerald-400 transition text-sm"
                    title="Record voice note"
                  >
                    🎙️
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auxiliary audio element to ensure remote media track audio output */}
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
    </div>
  );
}
